import { useEffect, useState } from 'react'
import { v4 as uuid } from 'uuid'
import CategoryPicker from './record/CategoryPicker'
import AmountInput from './record/AmountInput'
import OdometerInput from './record/OdometerInput'
import FuelFields from './record/FuelFields'
import DescriptionInput from './record/DescriptionInput'
import { defaultForm, type Category, type RecordForm } from './record/constants'
import { saveExpense, getCurrentOdometer, getFuelPrice } from '../lib/sync'
import { evalAmount } from '../lib/eval-amount'

type Status = 'idle' | 'saving' | 'saved' | 'offline-saved' | 'error'

export default function RecordPage() {
  const [lastOdo, setLastOdo] = useState(0)
  const [fuelPrice, setFuelPrice] = useState<number | null>(null)
  const [form, setForm] = useState<RecordForm>(() => defaultForm('油錢', 0))
  const [status, setStatus] = useState<Status>('idle')

  // 初始化：抓上次里程與當日油價
  useEffect(() => {
    getCurrentOdometer().then(km => {
      setLastOdo(km)
      setForm(f => ({ ...f, odometer_km: km > 0 ? String(km) : '' }))
    })
  }, [])

  useEffect(() => {
    getFuelPrice(form.date, form.fuel_grade).then(setFuelPrice)
  }, [form.date, form.fuel_grade])

  function set<K extends keyof RecordForm>(key: K, val: RecordForm[K]) {
    setForm(f => ({ ...f, [key]: val }))
  }

  function handleCategoryChange(cat: Category) {
    set('category', cat)
    // 換類別時清掉油錢專用欄位
    if (cat !== '油錢') {
      setForm(f => ({ ...f, category: cat, fuel_grade: '95', liters: '', is_full_tank: true }))
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const numAmount = evalAmount(form.amount)
    if (!numAmount) return
    setStatus('saving')

    const now = new Date().toISOString()
    const isFuel = form.category === '油錢'

    try {
      await saveExpense({
        id: uuid(),
        date: form.date,
        category: form.category,
        description: form.description || null,
        amount: numAmount,
        odometer_km: form.odometer_km ? parseInt(form.odometer_km, 10) : null,
        note: form.note || null,
        fuel_grade: isFuel ? form.fuel_grade : null,
        liters: isFuel && form.liters ? parseFloat(form.liters) : null,
        is_full_tank: isFuel ? form.is_full_tank : null,
        reminder_rule_id: null,
        created_at: now,
        updated_at: now,
        deleted_at: null,
      })

      const newOdo = form.odometer_km ? parseInt(form.odometer_km, 10) : lastOdo
      setLastOdo(newOdo)
      setForm(defaultForm(form.category, newOdo))
      setStatus(navigator.onLine ? 'saved' : 'offline-saved')
    } catch {
      setStatus('error')
    }

    setTimeout(() => setStatus('idle'), 2500)
  }

  const today = new Date().toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'short' })

  return (
    <form className="record-page" onSubmit={handleSubmit} noValidate>
      <header className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">記一筆</h1>
          <p className="page-header-sub">{today}</p>
        </div>
      </header>

      <CategoryPicker value={form.category} onChange={handleCategoryChange} />

      <AmountInput value={form.amount} onChange={v => set('amount', v)} />

      <OdometerInput
        value={form.odometer_km}
        lastOdometer={lastOdo}
        onChange={v => set('odometer_km', v)}
      />

      {form.category === '油錢' && (
        <FuelFields
          grade={form.fuel_grade}
          liters={form.liters}
          isFullTank={form.is_full_tank}
          amount={form.amount}
          fuelPrice={fuelPrice}
          onGrade={g => set('fuel_grade', g)}
          onLiters={v => set('liters', v)}
          onFullTank={v => set('is_full_tank', v)}
        />
      )}

      <DescriptionInput
        category={form.category}
        value={form.description}
        onChange={v => set('description', v)}
      />

      <div className="field">
        <label htmlFor="date">日期</label>
        <input
          id="date"
          type="date"
          value={form.date}
          onChange={e => set('date', e.target.value)}
        />
      </div>

      <button
        type="submit"
        className="submit-btn"
        disabled={!evalAmount(form.amount) || status === 'saving'}
      >
        {status === 'saving' ? '儲存中…' : '記錄'}
      </button>

      {status === 'saved' && <p className="status-msg success">已記錄</p>}
      {status === 'offline-saved' && <p className="status-msg warning">已記錄，連線後同步</p>}
      {status === 'error' && <p className="status-msg error">儲存失敗，請重試</p>}
    </form>
  )
}
